import { ComponentProps } from 'react';

import { dayLabel, monthTitle } from '../dates';
import { dayRole, monthGrid, Selection, shiftMonth } from '../monthGrid';
import {
  CalendarRoot,
  DayButton,
  IconButton,
  MonthHeader,
  MonthTitle,
  Weekday,
  WeekdayRow,
  WeekRow,
} from '../Operations.styles';

import { ChevronLeft, ChevronRight } from './icons';

type Props = {
  /** "YYYY-MM" */
  month: string;
  onMonthChange: (month: string) => void;
  /** Ops-zone today, from graphy (`meta.today`); null until loaded. */
  today: string | null;
  selection: Selection | null;
  /** Dates with at least one block (green dot). */
  marked: Set<string>;
  /** Future dates whose block still has a deactivated manager. */
  flagged?: Set<string>;
  onSelect: (date: string) => void;
  /** Dates that cannot be picked (the Repeat modal's past dates). */
  isDisabled?: (date: string) => boolean;
  /** Edit mode: dimmed and not clickable. */
  dimmed?: boolean;
  /** Repeat modal: a single date is a light circle, not a solid one. */
  lightSingle?: boolean;
  /** Root test id; also `<prefix>PrevButton`, `NextButton`, `Title`, `Dot-`, `Flag-`. */
  testIdPrefix: string;
  /** Day test id prefix; defaults to `<testIdPrefix>Day`. */
  dayTestIdPrefix?: string;
};

const WEEKDAYS = [
  ['S', 'Sunday'],
  ['M', 'Monday'],
  ['T', 'Tuesday'],
  ['W', 'Wednesday'],
  ['T', 'Thursday'],
  ['F', 'Friday'],
  ['S', 'Saturday'],
] as const;

type DayLook = ComponentProps<typeof DayButton>['look'];
type DayBand = ComponentProps<typeof DayButton>['band'];

// Precedence (spec §7): range start/end > range middle > selected > today.
const looks = (
  selection: Selection | null,
  date: string,
  today: string | null,
  lightSingle: boolean,
): { look: DayLook; band: DayBand } => {
  switch (dayRole(selection, date)) {
    case 'start':
      return { look: 'edge', band: 'right' };
    case 'end':
      return { look: 'edge', band: 'left' };
    case 'middle':
      return { look: 'middle', band: 'full' };
    case 'single':
      return { look: lightSingle ? 'singleLight' : 'single', band: 'none' };
    default:
      return { look: date === today ? 'today' : 'none', band: 'none' };
  }
};

// The Operations calendar (UI spec §7): Sunday-first month grid, today ring,
// selection fill, range band, green dots for dates with blocks. Also the
// date picker in the Repeat modal.
const MonthCalendar = ({
  month,
  onMonthChange,
  today,
  selection,
  marked,
  flagged,
  onSelect,
  isDisabled,
  dimmed = false,
  lightSingle = false,
  testIdPrefix,
  dayTestIdPrefix = `${testIdPrefix}Day`,
}: Props) => (
  <CalendarRoot
    dimmed={dimmed}
    aria-disabled={dimmed || undefined}
    data-testid={testIdPrefix}
    data-dimmed={dimmed}
  >
    <MonthHeader>
      <IconButton
        type="button"
        size="month"
        aria-label="Previous month"
        disabled={dimmed}
        data-testid={`${testIdPrefix}PrevButton`}
        onClick={() => onMonthChange(shiftMonth(month, -1))}
      >
        <ChevronLeft />
      </IconButton>
      <MonthTitle aria-live="polite" data-testid={`${testIdPrefix}Title`}>
        {monthTitle(month)}
      </MonthTitle>
      <IconButton
        type="button"
        size="month"
        aria-label="Next month"
        disabled={dimmed}
        data-testid={`${testIdPrefix}NextButton`}
        onClick={() => onMonthChange(shiftMonth(month, 1))}
      >
        <ChevronRight />
      </IconButton>
    </MonthHeader>
    <WeekdayRow aria-hidden="true">
      {WEEKDAYS.map(([letter, name]) => (
        <Weekday key={name} title={name}>
          {letter}
        </Weekday>
      ))}
    </WeekdayRow>
    <div role="group" aria-label={monthTitle(month)}>
      {monthGrid(month).map((week) => (
        <WeekRow key={week[0].date}>
          {week.map(({ date, inMonth }) => {
            const blocked = inMonth && (isDisabled?.(date) ?? false);
            const hasDot = inMonth && marked.has(date);
            const isFlagged = hasDot && (flagged?.has(date) ?? false);
            const { look, band } = inMonth
              ? looks(selection, date, today, lightSingle)
              : { look: 'none' as DayLook, band: 'none' as DayBand };
            const role = inMonth ? dayRole(selection, date) : null;
            const label = [
              dayLabel(date),
              date === today ? 'today' : null,
              role === 'middle' ? 'in selected range' : null,
              hasDot ? 'has schedules' : null,
              isFlagged ? 'has a deactivated manager' : null,
            ]
              .filter(Boolean)
              .join(', ');
            return (
              <DayButton
                key={date}
                type="button"
                look={look}
                band={band}
                outside={!inMonth}
                blocked={blocked}
                disabled={!inMonth || dimmed || blocked}
                aria-label={label}
                aria-pressed={
                  inMonth
                    ? role === 'single' || role === 'start' || role === 'end'
                    : undefined
                }
                aria-current={date === today ? 'date' : undefined}
                data-testid={`${dayTestIdPrefix}-${date}`}
                data-outside={!inMonth}
                data-selected={
                  role === 'single' || role === 'start' || role === 'end'
                }
                data-in-range={role === 'middle'}
                data-has-schedule={hasDot}
                onClick={() => onSelect(date)}
              >
                {band !== 'none' && <span data-layer="band" />}
                <span data-layer="circle" />
                <span data-layer="number">{Number(date.slice(8))}</span>
                {hasDot && (
                  <span
                    data-layer="dot"
                    data-flag={isFlagged}
                    data-testid={`${testIdPrefix}${isFlagged ? 'Flag' : 'Dot'}-${date}`}
                  />
                )}
              </DayButton>
            );
          })}
        </WeekRow>
      ))}
    </div>
  </CalendarRoot>
);

export default MonthCalendar;
